import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import ReactResizeDetector from 'react-resize-detector';
import {
  ScatterChart,
  ResponsiveContainer,
  ReferenceLine,
  Scatter,
  ReferenceDot,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
} from 'recharts';
import { CircularProgress } from '@material-ui/core';
import DataTable from './DataTable';
import { thunkFetchCorrelations } from './redux/thunk';
import { setActiveChart } from './redux/actions';
import {getMinMax, scaleCorrelation} from './utils';
import { range } from 'modules/aggregation/utils';
import { themeAnalysisCorrelationsStyles } from "./styles";
import { isMobileOnly } from 'react-device-detect';
import { useGAEffect } from '../../ga';
import InfoHint from '../../common/InfoHint';


const propTypes = {
  correlations: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  label: PropTypes.string,
  average_x: PropTypes.string,
  average_y: PropTypes.string,
  is_satisfaction: PropTypes.bool,
  is_correlation: PropTypes.bool,
  searchParams: PropTypes.object,
  fetchCorrelations: PropTypes.func.isRequired,
  setActiveChart: PropTypes.func.isRequired,
  ga: PropTypes.object.isRequired,
};

const useStyles = makeStyles(themeAnalysisCorrelationsStyles);

const AnalysisCorrelations = ({
  correlations,
  loading,
  label,
  average_x,
  average_y,
  is_satisfaction,
  is_correlation,
  fetchCorrelations,
  setActiveChart,
  searchParams,
  ga,
  filters,
}) => {
  const [graphWidth, setGraphWidth] = React.useState(0)
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth)
  const [isLandscape, setIsLandscape] = React.useState(window.innerWidth > window.innerHeight)
  const { group_id, project_id, survey_id, sdate, edate } = searchParams;
  const { chartGroup, textBold, showTableForWeb, buttonGroup, visibleOverflow } = useStyles();
  const [correlationData, setCorrelationData] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [npsHintPos, setNpsHintPos] = useState({ top: 0, left: 0 });
  const touchStartDistance = useRef(0);
  const touchStartZoomLevel = useRef(1);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const lastPanPos = useRef({ x: 0, y: 0 });
  const isMouseDragging = useRef(false);
  const mouseStartPos = useRef({ x: 0, y: 0 });
  const graphContainerRef = useRef(null);
  const isMouseOverChart = useRef(false);
  const npsLabelRef = useRef(null);
  const showSatisfactionHint = (label || '').includes('TOP2');

  useGAEffect(ga, "correlations", "相関分析")

  useEffect(() => {
    setActiveChart("graph-correlation");
    setCorrelationData(correlations.map((item) => ({ ...item, label: item.label ? item.label : item.body, status: true })));
    requestAnimationFrame(updateNpsHintPos);
    // eslint-disable-next-line
  }, [correlations]);

  useEffect(() => {
    fetchCorrelations({ ...searchParams, ...filters });
    // eslint-disable-next-line
  }, [fetchCorrelations, group_id, project_id, survey_id, sdate, edate, filters])

  useEffect(() => {
    updateNpsHintPos()
    window.addEventListener('resize', updateNpsHintPos)
    return () => window.removeEventListener('resize', updateNpsHintPos)
    // eslint-disable-next-line
  }, [graphWidth, isLandscape, label, zoomLevel, panOffset])

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [])

  // wheelイベントを非パッシブで登録
  useEffect(() => {
    const container = graphContainerRef.current;
    if (!container) return;

    const handleWheelNonPassive = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoomLevel = Math.min(Math.max(zoomLevel * delta, 1), 80);
      setZoomLevel(newZoomLevel);
      
      if (newZoomLevel === 1) {
        setPanOffset({ x: 0, y: 0 });
      }
      
      return false;
    };

    container.addEventListener('wheel', handleWheelNonPassive, { passive: false });
    
    return () => {
      container.removeEventListener('wheel', handleWheelNonPassive);
    };
  }, [zoomLevel])

  // マウスオーバー時のスクロール制御
  const handleMouseEnter = () => {
    isMouseOverChart.current = true;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollBarWidth}px`;
  };

  const handleMouseLeave = () => {
    isMouseOverChart.current = false;
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    handleMouseUp();
  };

  // クリーンアップ
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, []);

  const updateNpsHintPos = () => {
    const labelEl = npsLabelRef.current;
    const wrapperEl = graphContainerRef.current;
    if (!labelEl || !wrapperEl) return;

    const labelRect = labelEl.getBoundingClientRect();
    const wrapperRect = wrapperEl.getBoundingClientRect();
    const hintLeftOffset = -5; // nudge the hint slightly left of center (closer to text)
    setNpsHintPos({
      top: labelRect.bottom - wrapperRect.top + 8,
      left: labelRect.left - wrapperRect.left + labelRect.width / 2 + hintLeftOffset,
    });
  };

  const onResize = (width) => {
    const firstReferenceLine = document.getElementById('first-reference-line')
    if (firstReferenceLine) {
      setGraphWidth(firstReferenceLine.getBBox().width)
    }
    requestAnimationFrame(updateNpsHintPos)
    if (width) {
      setWindowWidth(width)
    }
  }

  const lineProps = (x, y, value, dx, dy, strokeWidth, position) => ({
    x, y,
    stroke: "#B7B7B7",
    strokeWidth,
    label: {
      position: !!position ? position : 'left',
      fill: '#796255',
      fontSize: 14,
      value, dx, dy,
    }
  })

  const rowNames = [label || "満足度", "相関係数", "回答数"];
  const rowKeys = ["x", "y", "count"];

  // Calculate points on Y coordinate
  const [domainMinY, scaledMaxY] = scaleCorrelation(getMinMax(correlations, ["y"]), [-1, 1])
  const domainMaxY = Math.min(scaledMaxY, 1)
  // const unitY = (domainMaxY - domainMinY)/8  // 現在使用されていない

  // Calculate points on X coordinate
  const [scaledMinX, scaledMaxX] = scaleCorrelation(getMinMax(correlations, ["x"]), [-10, 10])
  const domainMaxX = Math.min(scaledMaxX, label === "平均点" ? 10 : 100)
  const domainMinX = Math.max(scaledMinX, 0)
  // const unitX = (domainMaxX - domainMinX)/10  // 現在使用されていない


  const toggleButton = (i) => {
    correlationData[i].status = !correlationData[i].status
    setCorrelationData([...correlationData])
  }

  // タッチイベントハンドラー
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // ピンチズーム開始（常に現在の状態から開始）
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      touchStartDistance.current = distance;
      touchStartZoomLevel.current = zoomLevel;
      isPanning.current = false;
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      // パン開始（ズーム中のみ）
      const touch = e.touches[0];
      touchStartPos.current = { x: touch.clientX, y: touch.clientY };
      lastPanPos.current = { ...panOffset };
      isPanning.current = true;
    }
  };

  const handleTouchMove = (e) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    
    if (e.touches.length === 2 && touchStartDistance.current > 0) {
      // ピンチズーム
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      const scaleChange = distance / touchStartDistance.current;
      const newZoomLevel = Math.min(Math.max(touchStartZoomLevel.current * scaleChange, 1), 80);
      setZoomLevel(newZoomLevel);
      
      // ズームが1に戻ったらパンをリセット
      if (newZoomLevel === 1) {
        setPanOffset({ x: 0, y: 0 });
      }
    } else if (e.touches.length === 1 && isPanning.current && zoomLevel > 1) {
      // パン（移動）
      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartPos.current.x;
      const deltaY = touch.clientY - touchStartPos.current.y;
      
      // パン範囲の制限を追加
      const maxPan = (zoomLevel - 1) * 200;
      setPanOffset({
        x: Math.max(-maxPan, Math.min(maxPan, lastPanPos.current.x + deltaX)),
        y: Math.max(-maxPan, Math.min(maxPan, lastPanPos.current.y + deltaY))
      });
    }
  };

  const handleTouchEnd = () => {
    touchStartDistance.current = 0;
    isPanning.current = false;
  };

  // ダブルタップ/ダブルクリックでズームリセット
  const handleDoubleClick = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };


  // マウスドラッグ開始
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      isMouseDragging.current = true;
      mouseStartPos.current = { x: e.clientX, y: e.clientY };
      lastPanPos.current = { ...panOffset };
      if (e.cancelable) {
        e.preventDefault();
      }
    }
  };

  // マウスドラッグ中
  const handleMouseMove = (e) => {
    if (isMouseDragging.current && zoomLevel > 1) {
      const deltaX = e.clientX - mouseStartPos.current.x;
      const deltaY = e.clientY - mouseStartPos.current.y;
      
      const maxPan = (zoomLevel - 1) * 200;
      setPanOffset({
        x: Math.max(-maxPan, Math.min(maxPan, lastPanPos.current.x + deltaX)),
        y: Math.max(-maxPan, Math.min(maxPan, lastPanPos.current.y + deltaY))
      });
    }
  };

  // マウスドラッグ終了
  const handleMouseUp = () => {
    isMouseDragging.current = false;
  };

  const axisInfoOffsets = isMobileOnly
    ? { x: { offsetX: -10, offsetY: 0 }, y: { offsetX: 0, offsetY: 10 } }
    : { x: { offsetX: -12, offsetY: 0 }, y: { offsetX: 0, offsetY: 12 } };

  const AverageLineLabel = ({ value, axis, viewBox }) => {
    const { x = 0, y = 0, width = 0, height = 0 } = viewBox || {};
    const infoId = axis === 'x' ? 'correlation-satisfaction-average' : 'correlation-nps-coefficient-average';
    const hasValue = value !== undefined && value !== null && value !== '';
    const hideInfoHint =
      (axis === 'x' && Boolean(is_satisfaction)) ||
      (axis === 'y' && Boolean(is_correlation));
    const shouldShowInfoHint = !hideInfoHint;
    console.log('[AverageLineLabel] info hint visibility', { is_satisfaction, is_correlation, shouldShowInfoHint });

    if (axis === 'x') {
      const labelWidth = 80;
      const translateX = x - 33;
      const translateY = y - 40;
      return (
        <g transform={`translate(${translateX}, ${translateY})`}>
          <foreignObject width={labelWidth} height={32} style={{ overflow: 'visible' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, pointerEvents: 'auto' }}>
              {shouldShowInfoHint && <InfoHint infoId={infoId} dense placement="top" />}
              {hasValue && <span>{value}</span>}
            </div>
          </foreignObject>
        </g>
      );
    }

    // モバイル縦向きはラベルとiマークの余白をコンパクトにする
    const isPortrait = isMobileOnly && !isLandscape; // スマホ縦向き判定
    const labelWidth = isPortrait ? 80 : 90;
    const translateX = x + width - 20;
    const translateY = isPortrait ? y - 8 : y - 12;
    return (
      <g transform={`translate(${translateX}, ${translateY})`}>
        <foreignObject width={labelWidth} height={isPortrait ? 32 : 28} style={{ overflow: 'visible' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: isPortrait ? 'center' : 'center',
              justifyContent: 'center',
              gap: isPortrait ? 0 : 3,
              pointerEvents: 'auto',
              flexDirection: isPortrait ? 'column' : 'row', // スマホ縦向きは値の下にiマークを配置
            }}
          >
            {isPortrait ? (
              <>
                {hasValue && <span>{value}</span>}
                {shouldShowInfoHint && <InfoHint infoId={infoId} dense placement="top" />}
              </>
            ) : (
              <>
                {hasValue && <span>{value}</span>}
                {shouldShowInfoHint && <InfoHint infoId={infoId} dense placement="right" />}
              </>
            )}
          </div>
        </foreignObject>
      </g>
    );
  };

  const AxisInfoMarker = ({ cx, cy, infoId, placement = 'top', offsetX = 0, offsetY = 0 }) => {
    const size = 22;
    return (
      <foreignObject
        x={cx + offsetX - size / 2}
        y={cy + offsetY - size / 2}
        width={size}
        height={size}
        style={{ overflow: 'visible', pointerEvents: 'auto' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <InfoHint infoId={infoId} dense placement={placement} />
        </div>
      </foreignObject>
    );
  };

  const AxisInfoDots = () => (
    <>
      <ReferenceDot
        x={domainMaxX}
        y={0}
        r={0}
        ifOverflow="visible"
        isFront
        stroke="none"
        fill="none"
        shape={({ cx, cy }) => (
          <AxisInfoMarker
            cx={cx}
            cy={cy}
            infoId="correlation-satisfaction"
            placement="top"
            offsetX={axisInfoOffsets.x.offsetX}
            offsetY={axisInfoOffsets.x.offsetY}
          />
        )}
      />
      <ReferenceDot
        x={0}
        y={domainMaxY}
        r={0}
        ifOverflow="visible"
        isFront
        stroke="none"
        fill="none"
        shape={({ cx, cy }) => (
          <AxisInfoMarker
            cx={cx}
            cy={cy}
            infoId="correlation-nps-coefficient"
            placement="top"
            offsetX={axisInfoOffsets.y.offsetX}
            offsetY={axisInfoOffsets.y.offsetY}
          />
        )}
      />
    </>
  );

  return (
    <div style={{ marginRight: 10 }}>
      <div className={chartGroup}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: isMobileOnly ? 'center' : 'flex-start', paddingRight: 24 }}>
          <div className={buttonGroup} style={isMobileOnly ? { width: '100%', justifyContent: 'center' } : {}}>
            {
              correlationData.map((corr, index) => (
              <button key={index} className={corr.status ? '' : 'offButton'} style={isMobileOnly ? { width: 92 } : {}} onClick={() => {toggleButton(index)}}>{corr.label}</button>
              ))
            }
          </div>
        </div>
        {/* PC表示時もズーム機能付き */}
        {!isMobileOnly ? (
          <div 
          ref={graphContainerRef}
          style={{ 
            width: '100%', 
            height: 560,
            overflow: 'hidden',
            cursor: zoomLevel > 1 ? 'move' : 'default',
            overscrollBehavior: 'contain',
            touchAction: 'none',
            position: 'relative'
          }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onDoubleClick={handleDoubleClick}
          >
            <ResponsiveContainer 
              width='100%' 
              height={560} 
              id="graph-correlation" 
              className={visibleOverflow}
            >
              {(() => {
                // ズーム時のドメイン計算
                const centerX = (domainMaxX + domainMinX) / 2;
                const centerY = (domainMaxY + domainMinY) / 2;
                
                // 元のレンジ
                const originalRangeX = domainMaxX - domainMinX;
                const originalRangeY = domainMaxY - domainMinY;
                
                // グリッドを正方形に保つため、同じスケール比率を使用
                const rangeX = originalRangeX / zoomLevel;
                const rangeY = originalRangeY / zoomLevel;
                
                // パン時のオフセットを考慮
                const panOffsetX = panOffset.x * rangeX / 400;
                const panOffsetY = -panOffset.y * rangeY / 400;
                
                // ズーム後のドメイン
                let zoomedDomainMinX = centerX - rangeX / 2 - panOffsetX;
                let zoomedDomainMaxX = centerX + rangeX / 2 - panOffsetX;
                let zoomedDomainMinY = centerY - rangeY / 2 - panOffsetY;
                let zoomedDomainMaxY = centerY + rangeY / 2 - panOffsetY;
                
                // グリッド数を増やして密度を高める
                const baseGridY = 8;
                const baseGridX = 10;
                
                // ズームレベルに応じてグリッド数を増やす（最大2倍まで）
                const gridMultiplier = zoomLevel >= 2 ? 2 : 1;
                const gridCountY = baseGridY * gridMultiplier;
                const gridCountX = baseGridX * gridMultiplier;
                
                // 全体のドメインに対してグリッドを配置
                const gridUnitY = (domainMaxY - domainMinY) / gridCountY;
                const gridUnitX = (domainMaxX - domainMinX) / gridCountX;
                
                return (
                  <ScatterChart margin={{top: 60, right: 50, bottom: 60, left: 100}}>
                    <ReferenceLine y={average_y} stroke="#B7B7B7" strokeWidth={4} label={<AverageLineLabel value={average_y} axis="y" />}/>
                    <ReferenceLine {...lineProps(0, domainMaxY, domainMaxY.toFixed(3), 0, 6, 2)} id='first-reference-line'/>
                    <ReferenceLine {...lineProps(0, domainMinY, domainMinY.toFixed(3), 0, -6, 2)} />
                    <ReferenceLine {...lineProps(domainMinX, null, domainMinX, 9, 0, 2, 'bottom')} />
                    <ReferenceLine x={average_x || 0} stroke="#B7B7B7" strokeWidth={4} label={<AverageLineLabel value={average_x} axis="x" />}/>
                    <ReferenceLine {...lineProps(domainMaxX, null, domainMaxX, -19, 0, 2, 'bottom')} />
                    {range(gridCountY + 1).map(i => <ReferenceLine key={`S${i}`} {...lineProps(0, domainMinY + i*gridUnitY, "", 0, 0)} />)}
                    {range(gridCountX + 1).map(i => <ReferenceLine key={i} {...lineProps(domainMinX + i*gridUnitX, null, "", 0, 0)} />)}
                    <YAxis 
                      dataKey={'y'} 
                      type="number" 
                      name='相関係数' 
                      domain={[zoomedDomainMinY, zoomedDomainMaxY]} 
                      tickCount={0} 
                      hide={true}
                      allowDataOverflow={true}
                    />
                    <XAxis 
                      dataKey={'x'} 
                      type="number" 
                      name={label} 
                      domain={[zoomedDomainMinX, zoomedDomainMaxX]} 
                      tickCount={11} 
                      hide={true}
                      allowDataOverflow={true}
                    />
                    <foreignObject x="45%" y="95%" width="180" height="30" style={{ overflow: 'visible' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, pointerEvents: 'auto' }}>
                        <span className={textBold} style={{ color: '#1A0707' }}>満足度{label ? `(${label})` : ''}</span>
                        {showSatisfactionHint && <InfoHint infoId="correlation-satisfaction" dense placement="bottom" />}
                      </div>
                    </foreignObject>
                    <text ref={npsLabelRef} dx="18" y="200" rotate="-90" transform="rotate(90, 45, 180)" letterSpacing="5" textAnchor="middle">
                      <tspan x="135" dy="0.655em" className={textBold}>N P S 相関係数</tspan>
                    </text>
                    <g transform="translate(20, 150) rotate(-90)"><Arrow /></g>
                    <g transform="translate(20, 405) rotate(90)"><Arrow /></g>
                    <g transform="translate(192, 545) rotate(180)"><Arrow /></g>
                    <g transform={`translate(${graphWidth > 0 ? graphWidth : 500}, 545) rotate(0)`}><Arrow /></g>
                    <g transform="translate(110,67)"><CoordinateLabel text="優先課題" infoId="correlation-priority" isMobile={false} /></g>
                    <g transform="translate(110,461)"><CoordinateLabel text="注意観察" infoId="correlation-watch" isMobile={false} /></g>
                    <g transform={`translate(${(graphWidth > 0 ? graphWidth : 400) - 16}, 461)`}><CoordinateLabel text="基本維持" infoId="correlation-basic" right isMobile={false} labelOffset={-10} /></g>
                    <g transform={`translate(${(graphWidth > 0 ? graphWidth : 400) - 16}, 67)`}><CoordinateLabel text="維持強化" infoId="correlation-maintain" right isMobile={false} labelOffset={-10} /></g>
                    <Scatter data={correlationData} fill='#8884d8' shape={<CustomDot />} isAnimationActive={false}>
                      <LabelList dataKey="body" position="top" content={({ index, ...props }) => <QuestionAsLabel data={correlationData[index]} {...props} />} />
                    </Scatter>
                    <Tooltip cursor={{strokeDasharray: '3 3'}} content={<CustomTooltip label={label}/>}/>
                    <AxisInfoDots />
                  </ScatterChart>
                );
              })()}
            </ResponsiveContainer>
            {(!isMobileOnly || isLandscape) && (
              <div style={{ position: 'absolute', top: (npsHintPos.top || '60%'), left: (npsHintPos.left || 70), transform: 'translate(-50%, 0)', pointerEvents: 'auto', zIndex: 2 }}>
                <InfoHint infoId="correlation-nps-coefficient" dense placement="top" />
              </div>
            )}
          </div>
        ) : (
          /* スマートフォン表示時はズーム機能付き */
          <div 
            style={{ 
              width: '100%', 
              height: 560,
              overflow: 'hidden',
              touchAction: 'none'
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onDoubleClick={handleDoubleClick}
          >
            <ResponsiveContainer 
              width='100%' 
              height={560} 
              id="graph-correlation" 
              className={visibleOverflow}
            >
              {(() => {
                // ズーム時のドメイン計算
                const centerX = (domainMaxX + domainMinX) / 2;
                const centerY = (domainMaxY + domainMinY) / 2;
                
                // 元のレンジ
                const originalRangeX = domainMaxX - domainMinX;
                const originalRangeY = domainMaxY - domainMinY;
                
                // グリッドを正方形に保つため、同じスケール比率を使用
                const rangeX = originalRangeX / zoomLevel;
                const rangeY = originalRangeY / zoomLevel;
                
                
                // パン時のオフセットを考慮
                const panOffsetX = panOffset.x * rangeX / 400;
                const panOffsetY = -panOffset.y * rangeY / 400;
                
                // ズーム後のドメイン（制限なし）
                let zoomedDomainMinX = centerX - rangeX / 2 - panOffsetX;
                let zoomedDomainMaxX = centerX + rangeX / 2 - panOffsetX;
                let zoomedDomainMinY = centerY - rangeY / 2 - panOffsetY;
                let zoomedDomainMaxY = centerY + rangeY / 2 - panOffsetY;
                
                
                // グリッド数を増やして密度を高める（最大2段階まで）
                const baseGridY = 8;
                const baseGridX = 10;
                
                // ズームレベルに応じてグリッド数を増やす（最大2倍まで）
                const gridMultiplier = zoomLevel >= 2 ? 2 : 1;
                const gridCountY = baseGridY * gridMultiplier;
                const gridCountX = baseGridX * gridMultiplier;
                
                // 全体のドメインに対してグリッドを配置（絶対位置）
                const gridUnitY = (domainMaxY - domainMinY) / gridCountY;
                const gridUnitX = (domainMaxX - domainMinX) / gridCountX;
                
                return (
                  <ScatterChart margin={{top: 60, right: 50, bottom: 60, left: isLandscape ? 100 : 40}}>
                    <ReferenceLine y={average_y} stroke="#B7B7B7" strokeWidth={4} label={<AverageLineLabel value={average_y} axis="y" />}/>
                    <ReferenceLine {...lineProps(0, domainMaxY, domainMaxY.toFixed(3), 0, 6, 2)} id='first-reference-line'/>
                    <ReferenceLine {...lineProps(0, domainMinY, domainMinY.toFixed(3), 0, -6, 2)} />
                    <ReferenceLine {...lineProps(domainMinX, null, domainMinX, 9, 0, 2, 'bottom')} />
                    <ReferenceLine x={average_x || 0} stroke="#B7B7B7" strokeWidth={4} label={<AverageLineLabel value={average_x} axis="x" />}/>
                    <ReferenceLine {...lineProps(domainMaxX, null, domainMaxX, -19, 0, 2, 'bottom')} />
                    {range(gridCountY + 1).map(i => <ReferenceLine key={`S${i}`} {...lineProps(0, domainMinY + i*gridUnitY, "", 0, 0)} />)}
                    {range(gridCountX + 1).map(i => <ReferenceLine key={i} {...lineProps(domainMinX + i*gridUnitX, null, "", 0, 0)} />)}
                    <YAxis 
                      dataKey={'y'} 
                      type="number" 
                      name='相関係数' 
                      domain={[zoomedDomainMinY, zoomedDomainMaxY]} 
                      tickCount={0} 
                      hide={true}
                      allowDataOverflow={true}
                    />
                    <XAxis 
                      dataKey={'x'} 
                      type="number" 
                      name={label} 
                      domain={[zoomedDomainMinX, zoomedDomainMaxX]} 
                      tickCount={11} 
                      hide={true}
                      allowDataOverflow={true}
                    />
                    {/* スマホは左右矢印との間隔を均等にするためX座標を計算して中央寄せ */}
                    <foreignObject
                      x={isLandscape ? "38%" : (graphWidth > 0 ? Math.max(0, graphWidth / 2 - 60) : 120)} // モバイル時は少し右寄せして右矢印との間隔を詰める
                      y={isMobileOnly ? "92%" : "98%"}
                      width="190"
                      height={isMobileOnly ? "40" : "30"}
                      style={{ overflow: 'visible' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: isMobileOnly ? 4 : 8,
                          pointerEvents: 'auto',
                          flexDirection: isMobileOnly ? 'column' : 'row', // スマホ時は縦並びでラベル下にiマーク
                        }}
                      >
                        <span className={textBold}>満足度{label ? `(${label})` : ''}</span>
                        {/* モバイルは下、PCは横並び */}
                        {showSatisfactionHint && <InfoHint infoId="correlation-satisfaction" dense placement="bottom" />}
                      </div>
                    </foreignObject>
                    {isLandscape && <text ref={npsLabelRef} dx="18" y="200" rotate="-90" transform="rotate(90, 45, 180)" letterSpacing="5" textAnchor="middle">
                      <tspan x="135" dy="0.655em" className={textBold}>N P S 相関係数</tspan>
                    </text>}
                    {isLandscape && <g transform="translate(20, 150) rotate(-90)"><Arrow /></g>}
                    {isLandscape && <g transform="translate(20, 405) rotate(90)"><Arrow /></g>}
                    <g transform={`translate(${isLandscape ? 128 : 70}, 545) rotate(180)`}><ShortArrow /></g>
                    <g
                      transform={`translate(${isLandscape ? windowWidth - 30 : (graphWidth > 0 ? graphWidth + 14 : windowWidth - 30)}, 545) rotate(0)`}
                    >
                      <ShortArrow />
                    </g>
                    <g transform={`translate(${isLandscape ? 110 : 50}, 67)`}><CoordinateLabel text="優先課題" infoId="correlation-priority" isMobile={true} /></g>
                    <g transform={`translate(${isLandscape ? 110 : 50}, 461)`}><CoordinateLabel text="注意観察" infoId="correlation-watch" isMobile={true} /></g>
                    <g transform={`translate(${isLandscape ? (graphWidth > 0 ? graphWidth + 14 : 414) : (graphWidth > 0 ? graphWidth - 46 : 354)}, 461)`}><CoordinateLabel text="基本維持" infoId="correlation-basic" right isMobile={true} labelOffset={-8} /></g>
                    <g transform={`translate(${isLandscape ? (graphWidth > 0 ? graphWidth + 14 : 414) : (graphWidth > 0 ? graphWidth - 46 : 354)}, 67)`}><CoordinateLabel text="維持強化" infoId="correlation-maintain" right isMobile={true} labelOffset={-8} /></g>
                    <Scatter data={correlationData} fill='#8884d8' shape={<CustomDot />} isAnimationActive={false}>
                      <LabelList dataKey="body" position="top" content={({ index, ...props }) => <QuestionAsLabel data={correlationData[index]} {...props} />} />
                    </Scatter>
                    <Tooltip cursor={{strokeDasharray: '3 3'}} content={<CustomTooltip label={label}/>}/>
                    <AxisInfoDots />
                  </ScatterChart>
                );
              })()}
            </ResponsiveContainer>
            {(!isMobileOnly || isLandscape) && (
              <div
                style={
                  isMobileOnly
                    ? { position: 'absolute', top: '46.5%', left: 35, transform: 'translate(-50%, 0)', pointerEvents: 'auto', zIndex: 2 }
                    : { position: 'absolute', top: npsHintPos.top || '60%', left: npsHintPos.left || 60, transform: 'translate(-50%, 0)', pointerEvents: 'auto', zIndex: 2 }
                }
              >
                <InfoHint infoId="correlation-nps-coefficient" dense placement="top" />
              </div>
            )}
          </div>
        )}
      </div>
      <ReactResizeDetector handleWidth onResize={onResize} />

      <div 
        className={isMobileOnly ? 'data-table' : showTableForWeb} 
        style={isMobileOnly ? { 
          overflowX: 'auto', 
          width: '100vw', 
          marginLeft: '-12px', 
          paddingLeft: '12px', 
          paddingRight: '12px'
        } : { overflowX: 'auto', maxWidth: '100%' }}
      >
        {isMobileOnly && (
          <style>{`
            .data-table table {
              min-width: unset !important;
              width: calc(100vw - 54px) !important;
            }
            .data-table table th,
            .data-table table td {
              min-width: 80px !important;
            }
          `}</style>
        )}
        {loading ? <CircularProgress disableShrink />
          : <DataTable data={correlations} rowHeader="項目" rowKeys={rowKeys} rowNames={rowNames} rowWithSurfix={label === '平均点' ? -1 : 0} unitSurfix='%' pivot />}
      </div>
    </div>
  )
}

const QuestionAsLabel = props => {
  const { x, y, data } = props;
  const [actualWidth, setActualWidth] = useState(0);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const ref = useRef(null);
  const question = data.label;
  let rightEdge = 36
  const firstReferenceLine = document.getElementById('first-reference-line')
  if (firstReferenceLine) {
    const firstReferenceLineBox = firstReferenceLine.getBBox()
    rightEdge += firstReferenceLineBox.x + firstReferenceLineBox.width
  }
  // ラベルをポイントの真上中央に配置
  // const rectX = x - actualWidth/2  // 現在使用されていない
  const textX = x

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    if (ref.current) {
      try {
        const text = ref.current.getBBox();
        setActualWidth(text.width + 25);
      } catch (error) {
        console.warn('getBBox failed:', error);
        setActualWidth(100); // フォールバック値
      }
    }
    // eslint-disable-next-line
  }, [data]);

  if (!!!data.status) {
    return null;
  }

  // スマートフォン表示かつ縦向きの場合は10px、それ以外はデフォルト
  const fontSize = isMobileOnly && !isLandscape ? '10px' : undefined;
  
  // スマートフォン表示かつ縦向きの場合はサイズを0.8倍にする
  const scale = isMobileOnly && !isLandscape ? 0.8 : 1;
  const scaledWidth = actualWidth * scale;
  const scaledHeight = 35 * scale;
  const scaledRectX = x - scaledWidth/2;
  const scaledRectY = y - 55 * scale;
  const scaledTextY = y - 35 * scale;
  
  // ポリゴンの座標も調整
  const polygonY1 = y - 22 * scale;
  const polygonY2 = y - 12 * scale;
  const polygonXOffset = 10 * scale;
  const polygonXMid = 5 * scale;

  return (
    <g>
      <rect width={scaledWidth} height={scaledHeight} fill="#F2EEEC" x={scaledRectX} y={scaledRectY} rx={5} />
      <polygon fill="#F2EEEC" points={`${x} ${polygonY1}, ${x + polygonXOffset} ${polygonY1}, ${x + polygonXMid} ${polygonY2}`} />
      <text ref={ref} x={textX} y={scaledTextY} textAnchor="middle" dominantBaseline="middle" style={fontSize ? {fontSize} : undefined}>
        {question}
      </text>
    </g>
  )
};

const CustomTooltip = ({ payload }) => {
  if (!payload || payload.length === 0 || !payload[0].payload)
    return null

  const { body, x, y, count } = payload[0].payload;
  return (
    <div style={{ background: "#F2EEEC", padding: 8, borderRadius: 5, color: "black"}}>
      {body}
      <div>{payload[0].name}: {x}</div>
      <div>{payload[1].name}: {y}</div>
      <div>{'回答数'}: {count}</div>
    </div>
  )
}

const CustomDot = ({ cx, cy }) => (
  <circle cx={cx} cy={cy} r={8} stroke="black" strokeWidth={4} fillOpacity="0" />
)

const Arrow = () => <>
  <line x1="0" y1="0" x2="80" y2="0" stroke="#939393" strokeWidth="3" />
  <polygon points="80 -6, 93 0, 80 6" fill="#939393" />
</>

const ShortArrow = () => <>
  <line x1="0" y1="0" x2="20" y2="0" stroke="#939393" strokeWidth="3" />
  <polygon points="20 -6, 30 0, 20 6" fill="#939393" />
</>

const CoordinateLabel = ({text, right, isMobile, infoId}) => {
  const scale = isMobile ? 0.7 : 1;
  const iconSize = 22 * scale;
  const baseWidth = 90 * scale;
  const extraPadding = 12 * scale;
  const textWidth = (text ? text.length : 0) * (isMobile ? 12 : 15);
  const boxWidth = Math.max(baseWidth, textWidth + iconSize + extraPadding * 2);
  const boxHeight = 32 * scale;
  const strokeWidth = 4 * scale;
  const centerX = boxWidth / 2;
  const textX = 11 * scale;
  const textY = 22 * scale;
  const fontSize = isMobile ? '10px' : '16px';
  const iconX = boxWidth - iconSize - extraPadding;
  const iconY = (boxHeight - iconSize) / 2;
  
  return (
    <>
      <line x1={centerX} y1="0" x2={centerX} y2={boxHeight} stroke="#F2EEEC" strokeWidth={boxWidth} />
      <line x1={right ? boxWidth : 0} y1="0" x2={right ? boxWidth : 0} y2={boxHeight} stroke="#EC681A" strokeWidth={strokeWidth} />
      <text x={textX} y={textY} style={{fontSize}}>{text}</text>
      {infoId && (
        <foreignObject x={iconX} y={iconY} width={iconSize} height={iconSize} style={{ overflow: 'visible' }}>
          <div style={{ pointerEvents: 'auto' }}>
            <InfoHint infoId={infoId} dense placement="bottom" />
          </div>
        </foreignObject>
      )}
    </>
  );
}

AnalysisCorrelations.propTypes = propTypes;

export default connect(
  ({
    analysis: {
      correlations,
      loading,
      label,
      average_x,
      average_y,
      is_satisfaction,
      is_correlation,
    },
    global: { searchParams, filters },
    ga
  }) => ({
    correlations,
    loading,
    label,
    average_x,
    average_y,
    is_satisfaction,
    is_correlation,
    searchParams,
    ga,
    filters,
  }),
  dispatch => ({
    fetchCorrelations: searchParams => dispatch(thunkFetchCorrelations(searchParams)),
    setActiveChart: chart => dispatch(setActiveChart(chart)),
  })
)(AnalysisCorrelations);
